import React, {useState, useCallback, useEffect, useContext, useRef} from 'react';
import {Formik} from 'formik';
import {MY_COVERY_COOKIE} from 'constants/url.constants';
import {COVERY_DOMAIN} from 'constants/domain.constants';
import {isBrowser} from 'helpers/isBrowser';
import ErrorBoundary from 'modules/boundary/components/ErrorBoundary';
import {CookieContext} from '../cookie.context';
import {setCookie} from '../helpers/setCookie';
import {hasCookie} from '../helpers/hasCookie';
import {getCookie} from '../helpers/getCookie';
import {deleteCookie} from '../helpers/deleteCookie';
import {
	COOKIE_POLICY_ACCEPTED,
	IS_COUNTER_EXPIRED,
	COOKIE_POLICY_USER_ID,
	STATISTIC_SCRIPTS_ADDED,
	ADVERTISING_SCRIPTS_ADDED,
	COOKIE_SCRIPT_INITIALIZED,
} from '../cookie.constants';
import {PopupCookie as PopupCookieView} from '../components/PopupCookie';
import {PopupWrapper} from '../components/PopupWrapper';
import {CheckboxList} from '../components/CheckboxesList';
import {ButtonsContainer} from '../components/ButtonsContainer';
import { useRecaptcha } from './RecaptchaProvider';

export function PopupCookie(props) {
	const {
		list,
		checkboxes,
	} = props;

	const providerRecaptcha = useRecaptcha();
	const crypto = typeof window !== 'undefined' ? window.crypto.randomUUID() : '';
	const formRef = useRef(null);
	const userIdRef = useRef(getCookie(COOKIE_POLICY_USER_ID) || null);
	let userId = userIdRef.current;

	const hasCookiePolicy = isBrowser() ? hasCookie(COOKIE_POLICY_USER_ID) : true;
	const cookieScriptInitialized = isBrowser() ? hasCookie(COOKIE_SCRIPT_INITIALIZED) : true;
	const {setStatistics, setAdvertising} = useContext(CookieContext);
	const [shouldHideCookies, setHideCookies] = useState(hasCookiePolicy);
	const [isCounterExpired, setCounterExpired] = useState(hasCookie(IS_COUNTER_EXPIRED));
	const [isSettingsOpen, setSettingsOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [cookies, setCookies] = useState(['essential']);

	const setEssentialScripts = () => {
		const expirationDate = new Date();
		expirationDate.setFullYear(expirationDate.getFullYear() + 1);
		setCookie(COOKIE_POLICY_ACCEPTED, JSON.stringify(true), expirationDate, COVERY_DOMAIN);
		setCookie(COOKIE_POLICY_USER_ID, userId, expirationDate, COVERY_DOMAIN);
	};

	const cookieHandlers = [
		{
			name: 'advertising',
			flag: ADVERTISING_SCRIPTS_ADDED,
			setFunction: setAdvertising,
		},
		{
			name: 'statistics',
			flag: STATISTIC_SCRIPTS_ADDED,
			setFunction: setStatistics,
		},
	];

	const getUserCookie = async (userId) => {
		setIsLoading(true);
		const params = new URLSearchParams({
			user_id: userId,
		}).toString();

		fetch(`${MY_COVERY_COOKIE}?${params}`, {
			method: 'GET',
			mode: 'cors',
		})
			.then((response) => response.json())
			.then((data) => {
				if (data && data.cookies) {
					setCookies(data.cookies);
					if (data.cookies.includes('essential')) {
						setEssentialScripts();
					}
					cookieHandlers.forEach(({ name, flag, setFunction }) => {
						if (data.cookies.includes(name)) {
							if (!hasCookie(flag)) {
								setFunction();
							}
						} else {
							deleteCookie(flag, COVERY_DOMAIN);
						}
					});
				} else {
					setHideCookies(false);
				}
			})
			.catch((response) => {
				setCookies(['essential']);
				setEssentialScripts();
				deleteCookie(ADVERTISING_SCRIPTS_ADDED, COVERY_DOMAIN);
				deleteCookie(STATISTIC_SCRIPTS_ADDED, COVERY_DOMAIN);
			})
			.finally(() => {
				setCookie(COOKIE_SCRIPT_INITIALIZED, JSON.stringify(true), null);
				setIsLoading(false);
			});
	};

	const handleCounterExpired = useCallback(() => {
		setCounterExpired(true);
		setCookie(IS_COUNTER_EXPIRED, JSON.stringify(true), null, COVERY_DOMAIN);
	}, []);

	const handleToggleSettings = useCallback(() => {
		setSettingsOpen((isSettingsOpen) => !isSettingsOpen);
	}, []);

	const createHandleSubmit = () => {
		const values = formRef.current.values;
		if (values.cookie.includes('essential')) {
			setEssentialScripts();
		}
		cookieHandlers.forEach(({ name, flag, setFunction }) => {
			if (values.cookie.includes(name)) {
				if (!hasCookie(flag)) {
					setFunction();
				}
			} else {
				deleteCookie(flag, COVERY_DOMAIN);
			}
		});
		const formData = new FormData();
		Object.keys(values).forEach((key) => {
			formData.append(key, values[key]);
		});

		fetch(MY_COVERY_COOKIE, {
			method: 'POST',
			body: formData,
			mode: 'cors',
		})
			.then((response) => response.json())
			.then((data) => {
				setHideCookies(true);
			})
			.finally(() => {
				if (window.location.pathname === '/cookie-settings/') {
					window.location.reload();
				}
				setIsLoading(false);
			});
	};

	const handleUserAction = () => (async () => {
		if (!userId) {
			userId = crypto;
		}
		formRef.current.setFieldValue('user_id', userId);
		setTimeout(createHandleSubmit, 0);
	});

	const handleAcceptAllCookie = useCallback(() => {
		if (isLoading) return;
		formRef.current.setFieldValue('cookie', ['essential', 'advertising', 'statistics']);
		setTimeout(() => {
			formRef.current.submitForm();
		}, 0);
	}, [isLoading]);

	const handleAcceptSelected = useCallback(() => {
		if (isLoading) return;
		formRef.current.submitForm();
	}, [isLoading]);

	const handleSubmit = handleUserAction(userId);

	useEffect(() => {
		const onLoad = () => {
			if (userId && !cookieScriptInitialized) {
				getUserCookie(userId);
			}
		};
		if (document.readyState === 'complete') {
			onLoad();
		} else {
			window.addEventListener('load', onLoad);
		}
		return () => {
			window.removeEventListener('load', onLoad);
		};
	}, []);

	if (shouldHideCookies) return null;
	return (
		<PopupWrapper shouldDisplayBackdrop={!isCounterExpired}>
			<PopupCookieView
				isSettingsOpen={isSettingsOpen}
				isCounterExpired={isCounterExpired}
				handleToggleSettings={handleToggleSettings}
				handleCounterExpired={handleCounterExpired}
				list={list}
			>
				<Formik
					initialValues={{
						cookie: cookies,
					}}
					enableReinitialize
					onSubmit={handleSubmit}
				>
					{(form) => {
						formRef.current = form;
						return <CheckboxList
							form={form}
							checkboxesList={checkboxes}
							isSettingsOpen={isSettingsOpen}
							prefix="popup"
						/>;
					}}
				</Formik>
			</PopupCookieView>
			<ErrorBoundary>
				<ButtonsContainer
					isSettingsOpen={isSettingsOpen}
					onToggleSettings={handleToggleSettings}
					onAcceptAllCookie={handleAcceptAllCookie}
					onAcceptSelected={handleAcceptSelected}
					isDisabled={isLoading || !providerRecaptcha?.isReady}
				/>
			</ErrorBoundary>
		</PopupWrapper>
	);
}
